import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/vue-query";
import { nanoid } from "nanoid";
import { Block, type BlockEntity } from "~/src/api";
import { getAllBlocks } from "~/src/api/utils/block";

export function useBlockedUsers() {
  const queryClient = useQueryClient();
  const user = useLoggedUser();
  const queryKey: QueryKey = ["blocked-users"];

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      return await getAllBlocks(getTokenOrThrow);
    },
    initialData: [],
  });

  const createBlockMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const block = await Block.createBlock(
        targetUserId,
        await getTokenOrThrow(),
      );
      return block;
    },
    onMutate: (targetUserId) => {
      if (!user.value) return;

      const tempBlockId = addTargetUserToBlocksQuery(
        queryClient,
        queryKey,
        user.value.id,
        targetUserId,
      );

      return tempBlockId;
    },
    onError: (_error, targetUserId, tempBlockId) => {
      if (!tempBlockId) return;
      removeBlockFromQuery(queryClient, queryKey, tempBlockId);
    },
    onSuccess: (block, targetUserId, tempBlockId) => {
      updateBlockIdOnQuery(queryClient, queryKey, tempBlockId, block.id);
    },
  });

  const removeBlockMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      const block = getBlockFromQuery(queryClient, queryKey, targetUserId);
      await Block.removeBlock(targetUserId, await getTokenOrThrow());
      return block;
    },
    onMutate: (targetUserId) => {
      const block = getBlockFromQuery(queryClient, queryKey, targetUserId);
      if (!block) return;
      removeBlockFromQuery(queryClient, queryKey, block.id);
      return block;
    },
    onError: (_error, _targetUserId, block) => {
      if (!block) return;
      addBlockToQuery(queryClient, queryKey, block);
    },
  });

  const removeBlockBatchMutation = useMutation({
    mutationFn: async (targetUserIds: string[]) => {
      await Promise.all(
        targetUserIds.map(async (targetUserId) =>
          Block.removeBlock(targetUserId, await getTokenOrThrow()),
        ),
      );
    },
    onMutate: (targetUserIds) => {
      const blocks = targetUserIds
        .map((targetUserId) =>
          getBlockFromQuery(queryClient, queryKey, targetUserId),
        )
        .filter(onlyTruthys);
      blocks.forEach((block) =>
        removeBlockFromQuery(queryClient, queryKey, block.id),
      );
      return blocks;
    },
    onError: (_error, _targetUserIds, blocks) => {
      if (!blocks) return;
      blocks.forEach((block) => addBlockToQuery(queryClient, queryKey, block));
    },
  });

  return {
    query,
    createBlockMutation,
    removeBlockMutation,
    removeBlockBatchMutation,
  };
}

function getBlockFromQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  targetUserId: string,
) {
  const blocks = queryClient.getQueryData(queryKey) as
    | BlockEntity[]
    | undefined;
  return blocks?.find((b) =>
    b.relationships.find((u) => u.id === targetUserId),
  );
}

function addTargetUserToBlocksQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  loggedUserId: string,
  targetUserId: string,
) {
  const tempBlockId = nanoid();

  queryClient.setQueryData(queryKey, (oldData: BlockEntity[]) => {
    const newData = [...oldData];

    newData.push({
      attributes: {
        createdAt: new Date().toISOString(),
        updatedAt: null,
      },
      id: tempBlockId,
      relationships: [
        { id: loggedUserId, type: "user" },
        { id: targetUserId, type: "user" },
      ],
      type: "block",
    });

    return newData;
  });

  return tempBlockId;
}

function addBlockToQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  block: BlockEntity,
) {
  queryClient.setQueryData(queryKey, (oldData: BlockEntity[]) => {
    const newData = [...oldData];
    newData.push(block);
    return newData;
  });
}

function removeBlockFromQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  blockId: string,
) {
  queryClient.setQueryData(queryKey, (oldData: BlockEntity[]) => {
    const newData = [...oldData].filter((b) => b.id !== blockId);

    return newData;
  });
}

function updateBlockIdOnQuery(
  queryClient: QueryClient,
  queryKey: QueryKey,
  oldBlockId: string,
  newBlockId: string,
) {
  queryClient.setQueryData(queryKey, (oldData: BlockEntity[]) => {
    const block = oldData.find((b) => b.id === oldBlockId);
    if (!block) return oldData;
    block.id = newBlockId;

    return [...oldData];
  });
}
